import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  analytics: 'Analytics',
  associates: 'Associates',
  interviews: 'Interviews',
  'review-queue': 'Review Queue',
  review: 'Review Interview',
  questions: 'Questions',
  templates: 'Templates',
  reports: 'Reports',
  'audit-logs': 'Audit Logs',
  settings: 'Settings',
  journey: 'Journey Analytics',
  'iq-questions': 'IQ Questions',
};

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Don't show breadcrumbs on dashboard or login
  if (pathSegments.length === 0 || pathSegments[0] === 'login' || location.pathname === '/dashboard') {
    return null;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/dashboard' },
  ];

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    const isId = /^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);

    // Skip IDs in breadcrumbs unless it's the last segment
    if (isId && index < pathSegments.length - 1) {
      return;
    }

    const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);

    // Replace IDs with descriptive text
    if (isId) {
      const previousSegment = pathSegments[index - 1];
      if (previousSegment === 'associates') {
        breadcrumbs.push({ label: 'Associate Profile', path: currentPath });
      } else if (previousSegment === 'templates') {
        breadcrumbs.push({ label: 'Edit Template', path: currentPath });
      } else if (previousSegment === 'review') {
        breadcrumbs.push({ label: 'Review Details', path: currentPath });
      } else if (previousSegment === 'interview' || previousSegment === 'interviews') {
        breadcrumbs.push({ label: 'Interview Details', path: currentPath });
      } else {
        breadcrumbs.push({ label: `Details`, path: currentPath });
      }
    } else {
      // Don't make parent segments clickable if they're not valid routes on their own
      const nextSegment = pathSegments[index + 1];
      const nextIsId = nextSegment && (/^\d+$/.test(nextSegment) || /^[0-9a-f]{8}-/i.test(nextSegment));
      if (nextIsId && ['review', 'interview', 'interviews'].includes(segment)) {
        // Skip this segment - it will be represented by the ID breadcrumb
        return;
      }
      breadcrumbs.push({ label, path: currentPath });
    }
  });

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center space-x-2 text-sm">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const isFirst = index === 0;

          return (
            <li key={crumb.path} className="flex items-center">
              {index > 0 && (
                <ChevronRight
                  className="mx-2 h-4 w-4 text-white/30 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
              {isLast ? (
                <span className="font-medium text-white" aria-current="page">
                  {isFirst ? <Home className="h-4 w-4" aria-label={crumb.label} /> : crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-white/60 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-violet-400 rounded px-1"
                >
                  {isFirst ? <Home className="h-4 w-4" aria-label={crumb.label} /> : crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
